import React, { useCallback, useState, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToggleFieldForm } from '@traquility/ui/diary';
import { IconPickerContainer } from './IconPickerContainer';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../ui/common/Dropdown';
import type {
  ToggleFieldFormData,
  ToggleFieldFormLabels,
  ToggleFieldFormContainerProps,
  Category,
  FieldSet,
  Goal,
} from '../shared/types/toggleFieldForm';

export type { ToggleFieldFormContainerProps };

// Create a query client for icon picker
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes
    },
  },
});

const CREATE_TOGGLE_FIELD_MUTATION = `
  mutation CreateToggleField($input: CreateToggleFieldInput!) {
    createToggleField(input: $input) {
      toggleField {
        id
        name
        icon
        score
      }
      errors
    }
  }
`;

const UPDATE_TOGGLE_FIELD_MUTATION = `
  mutation UpdateToggleField($input: UpdateToggleFieldInput!) {
    updateToggleField(input: $input) {
      toggleField {
        id
        name
        icon
        score
      }
      errors
    }
  }
`;

interface ToggleFieldDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDelete: (convertActivities: boolean) => void;
  labels: ToggleFieldFormLabels;
  isDeleting: boolean;
}

function ToggleFieldDeleteModal({
  isOpen,
  onClose,
  onDelete,
  labels: l,
  isDeleting,
}: ToggleFieldDeleteModalProps) {
  const [convertActivities, setConvertActivities] = useState(true);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.deleteTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
          </div>
          <div className="modal-body">
            <div className="form-check mb-2">
              <input
                type="radio"
                className="form-check-input"
                id="convert-activities-true"
                name="convert-activities"
                checked={convertActivities}
                onChange={() => setConvertActivities(true)}
              />
              <label className="form-check-label" htmlFor="convert-activities-true">
                {l.convertActivitiesLabel}
              </label>
              <div className="form-text">{l.convertActivitiesHelp}</div>
            </div>
            <div className="form-check">
              <input
                type="radio"
                className="form-check-input"
                id="convert-activities-false"
                name="convert-activities"
                checked={!convertActivities}
                onChange={() => setConvertActivities(false)}
              />
              <label className="form-check-label text-danger" htmlFor="convert-activities-false">
                {l.deleteActivitiesLabel}
              </label>
              <div className="form-text text-danger">{l.deleteActivitiesHelp}</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isDeleting}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => onDelete(convertActivities)}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.deleteButton}
                </>
              ) : (
                l.deleteButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChangeCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (categoryId: string) => void;
  categories: Category[];
  currentCategoryId?: string;
  labels: ToggleFieldFormLabels;
  isChanging: boolean;
}

function ChangeCategoryModal({
  isOpen,
  onClose,
  onConfirm,
  categories,
  currentCategoryId,
  labels: l,
  isChanging,
}: ChangeCategoryModalProps) {
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-folder me-2"></i>
              {l.changeCategoryTitle}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isChanging}></button>
          </div>
          <div className="modal-body">
            <div className="form-floating mb-3">
              <select
                className="form-select"
                id="new-category-select"
                value={selectedCategoryId}
                onChange={(e) => setSelectedCategoryId(e.target.value)}
              >
                <option value="">— {l.selectCategory} —</option>
                {categories.map((cat) => (
                  <option
                    key={cat.id}
                    value={cat.id}
                    disabled={cat.id === currentCategoryId}
                    style={cat.id === currentCategoryId ? { fontStyle: 'italic', color: '#6c757d' } : {}}
                  >
                    {cat.name}{cat.id === currentCategoryId ? ` (${l.current})` : ''}
                  </option>
                ))}
              </select>
              <label htmlFor="new-category-select">{l.category}</label>
            </div>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              {l.changeCategoryHelp}
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isChanging}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onConfirm(selectedCategoryId)}
              disabled={isChanging || !selectedCategoryId}
            >
              {isChanging ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.moveToCategory}
                </>
              ) : (
                l.moveToCategory
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface MoveToFieldSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (fieldSetId: string) => void;
  fieldSets: FieldSet[];
  labels: ToggleFieldFormLabels;
  isMoving: boolean;
}

function MoveToFieldSetModal({
  isOpen,
  onClose,
  onConfirm,
  fieldSets,
  labels: l,
  isMoving,
}: MoveToFieldSetModalProps) {
  const [selectedFieldSetId, setSelectedFieldSetId] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedFieldSetId('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{l.moveToFieldSetTitle}</h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isMoving}></button>
          </div>
          <div className="modal-body">
            <div className="form-floating">
              <select
                className="form-select"
                id="field-set-select"
                value={selectedFieldSetId}
                onChange={(e) => setSelectedFieldSetId(e.target.value)}
              >
                <option value="">— {l.selectFieldSet} —</option>
                {fieldSets.map((fs) => (
                  <option key={fs.id} value={fs.id}>
                    {fs.name}
                  </option>
                ))}
              </select>
              <label htmlFor="field-set-select">{l.selectFieldSet}</label>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isMoving}>
              {l.cancel}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onConfirm(selectedFieldSetId)}
              disabled={isMoving || !selectedFieldSetId}
            >
              {isMoving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {l.moveButton}
                </>
              ) : (
                l.moveButton
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface GoalCardProps {
  goal?: Goal | null;
  labels: ToggleFieldFormLabels;
  goalNewUrl?: string;
  goalEditUrl?: string;
  goalDeleteUrl?: string;
  fieldSetId?: string;
  editFieldSetUrl?: string;
  csrfToken: string;
}

function GoalCard({
  goal,
  labels: l,
  goalNewUrl,
  goalEditUrl,
  goalDeleteUrl,
  fieldSetId,
  editFieldSetUrl,
  csrfToken,
}: GoalCardProps) {
  // If in a field set, show info message instead
  if (fieldSetId) {
    return (
      <div className="alert alert-info my-4">
        <i className="bi bi-info-circle"></i>
        {' '}{l.fieldInFieldSetMessage},{' '}
        <a href={editFieldSetUrl} className="alert-link">{l.editFieldSet}</a>
      </div>
    );
  }

  return (
    <div className="card my-4">
      <div className="card-header d-flex align-items-center">
        <h5 className="card-title mb-0 me-auto">
          <i className="bi bi-bullseye"></i>
          {' '}{l.goalTitle}
        </h5>
      </div>
      <div className="card-body">
        {goal ? (
          <div className="d-flex align-items-center mb-3">
            <div className="flex-grow-1">
              <div className="fw-bold">
                {l.goalTarget}: {goal.targetCount} {goal.period}
              </div>
              <small className="text-muted">{l.goalHelp}</small>
            </div>
            <div className="ms-3 d-flex gap-2">
              <a href={goalEditUrl} className="btn btn-sm btn-outline-primary" title={l.edit}>
                <i className="bi bi-pencil"></i>
                <span className="d-none d-sm-inline ms-1">{l.edit}</span>
              </a>
              <form action={goalDeleteUrl} method="post" style={{ display: 'inline' }}>
                <input type="hidden" name="_method" value="delete" />
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <button
                  type="submit"
                  className="btn btn-sm btn-outline-danger"
                  title={l.remove}
                  onClick={(e) => {
                    if (!confirm(l.deleteTitle + '?')) {
                      e.preventDefault();
                    }
                  }}
                >
                  <i className="bi bi-trash"></i>
                  <span className="d-none d-sm-inline ms-1">{l.remove}</span>
                </button>
              </form>
            </div>
          </div>
        ) : (
          <>
            <p className="text-muted mb-3">{l.goalHelp}</p>
            <a href={goalNewUrl} className="btn btn-primary">
              <i className="bi bi-plus-circle"></i>
              {' '}{l.setGoal}
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function ToggleFieldFormContainerInner({
  graphqlEndpoint,
  csrfToken,
  initialData,
  backUrl,
  successUrl,
  labels,
  errors: initialErrors = {},
  mode,
  categories = [],
  fieldSets = [],
  showCategorySelect = false,
  additionalActivityName,
  currentFieldSetId,
  goal,
  goalNewUrl,
  goalEditUrl,
  goalDeleteUrl,
  editFieldSetUrl,
  convertToAdditionalUrl,
}: ToggleFieldFormContainerProps) {
  const [errors, setErrors] = useState<Record<string, string[]>>(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [formIcon, setFormIcon] = useState(initialData.icon);
  const [formScore, setFormScore] = useState(initialData.score);

  // Edit mode modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangeCategoryModal, setShowChangeCategoryModal] = useState(false);
  const [showMoveToFieldSetModal, setShowMoveToFieldSetModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingCategory, setIsChangingCategory] = useState(false);
  const [isMovingToFieldSet, setIsMovingToFieldSet] = useState(false);

  const handleSubmit = useCallback(async (data: ToggleFieldFormData) => {
    setIsSubmitting(true);
    setErrors({});

    // Merge current icon/score from state
    const submitData = { ...data, icon: formIcon, score: formScore };

    const isEdit = mode === 'edit';
    const mutation = isEdit ? UPDATE_TOGGLE_FIELD_MUTATION : CREATE_TOGGLE_FIELD_MUTATION;
    const variables = isEdit
      ? {
          input: {
            id: submitData.id,
            name: submitData.name,
            icon: submitData.icon,
            score: submitData.score,
          },
        }
      : {
          input: {
            name: additionalActivityName || submitData.name,
            icon: submitData.icon,
            score: submitData.score,
            categoryId: submitData.categoryId,
            fieldSetId: submitData.fieldSetId || null,
            additionalActivityName: additionalActivityName || null,
          },
        };

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: mutation, variables }),
      });

      if (!response.ok) {
        throw new Error('Network error');
      }

      const result = await response.json();

      if (result.errors) {
        setErrors({ base: result.errors.map((e: { message: string }) => e.message) });
        setIsSubmitting(false);
        return;
      }

      const mutationResult = isEdit ? result.data.updateToggleField : result.data.createToggleField;

      if (mutationResult.errors && mutationResult.errors.length > 0) {
        setErrors({ base: mutationResult.errors });
        setIsSubmitting(false);
        return;
      }

      // Success - redirect
      window.location.href = successUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsSubmitting(false);
    }
  }, [graphqlEndpoint, csrfToken, successUrl, mode, formIcon, formScore, additionalActivityName]);

  const handleCancel = useCallback(() => {
    window.location.href = backUrl;
  }, [backUrl]);

  const handleIconPickerOpen = useCallback(() => {
    setShowIconPicker(true);
  }, []);

  const handleIconSelect = useCallback((iconKey: string, score: number) => {
    setFormIcon(iconKey);
    setFormScore(score);
    setShowIconPicker(false);
  }, []);

  const handleScoreChange = useCallback((score: number) => {
    setFormScore(score);
  }, []);

  const handleDelete = useCallback(async (convertActivities: boolean) => {
    setIsDeleting(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation DeleteToggleField($input: DeleteToggleFieldInput!) {
              deleteToggleField(input: $input) {
                success
                errors
              }
            }
          `,
          variables: {
            input: {
              id: initialData.id,
              convertActivities,
            },
          },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.deleteToggleField.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.deleteToggleField.errors;
        setErrors({ base: errorMessages });
        setIsDeleting(false);
        setShowDeleteModal(false);
        return;
      }

      // Success - redirect to back URL
      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsDeleting(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  const handleChangeCategory = useCallback(async (newCategoryId: string) => {
    setIsChangingCategory(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation ChangeToggleFieldCategory($input: ChangeToggleFieldCategoryInput!) {
              changeToggleFieldCategory(input: $input) {
                toggleField {
                  id
                }
                errors
              }
            }
          `,
          variables: {
            input: {
              id: initialData.id,
              newCategoryId,
            },
          },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.changeToggleFieldCategory.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.changeToggleFieldCategory.errors;
        setErrors({ base: errorMessages });
        setIsChangingCategory(false);
        setShowChangeCategoryModal(false);
        return;
      }

      // Success - reload page to show updated category
      window.location.reload();
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsChangingCategory(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id]);

  const handleMoveToFieldSet = useCallback(async (fieldSetId: string) => {
    setIsMovingToFieldSet(true);

    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation MoveToggleFieldToFieldSet($input: MoveToggleFieldToFieldSetInput!) {
              moveToggleFieldToFieldSet(input: $input) {
                toggleField {
                  id
                }
                errors
              }
            }
          `,
          variables: {
            input: {
              id: initialData.id,
              fieldSetId,
            },
          },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.moveToggleFieldToFieldSet.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.moveToggleFieldToFieldSet.errors;
        setErrors({ base: errorMessages });
        setIsMovingToFieldSet(false);
        setShowMoveToFieldSetModal(false);
        return;
      }

      // Success - redirect to back URL (field set edit page)
      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
      setIsMovingToFieldSet(false);
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  const handleRemoveFromFieldSet = useCallback(async () => {
    try {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: `
            mutation RemoveToggleFieldFromFieldSet($input: RemoveToggleFieldFromFieldSetInput!) {
              removeToggleFieldFromFieldSet(input: $input) {
                toggleField {
                  id
                }
                errors
              }
            }
          `,
          variables: {
            input: {
              id: initialData.id,
            },
          },
        }),
      });

      const result = await response.json();

      if (result.errors || (result.data.removeToggleFieldFromFieldSet.errors?.length > 0)) {
        const errorMessages = result.errors?.map((e: { message: string }) => e.message) ||
          result.data.removeToggleFieldFromFieldSet.errors;
        setErrors({ base: errorMessages });
        return;
      }

      // Success - redirect to back URL
      window.location.href = backUrl;
    } catch {
      setErrors({ base: ['An unexpected error occurred. Please try again.'] });
    }
  }, [graphqlEndpoint, csrfToken, initialData.id, backUrl]);

  // Prepare form data with current icon/score
  const formDataWithIcon = {
    ...initialData,
    icon: formIcon,
    score: formScore,
  };

  return (
    <>
      {/* Edit mode: More menu and actions */}
      {mode === 'edit' && (
        <div className="position-absolute" style={{ top: 10, right: 10 }}>
          <Dropdown>
            <DropdownTrigger className="btn btn-link text-secondary">
              <i className="bi bi-three-dots"></i>
            </DropdownTrigger>
            <DropdownMenu align="end">
              {currentFieldSetId ? (
                <DropdownItem onClick={handleRemoveFromFieldSet}>
                  {labels.removeFromFieldSet}
                </DropdownItem>
              ) : (
                <>
                  <DropdownItem onClick={() => setShowMoveToFieldSetModal(true)}>
                    {labels.moveToFieldSet}
                  </DropdownItem>
                  <DropdownItem onClick={() => setShowChangeCategoryModal(true)}>
                    {labels.changeCategory}
                  </DropdownItem>
                </>
              )}
              {convertToAdditionalUrl && (
                <DropdownItem
                  href={convertToAdditionalUrl}
                  data-turbo-method="post"
                  data-turbo-confirm={labels.convertToAdditional + '?'}
                >
                  {labels.convertToAdditional}
                </DropdownItem>
              )}
              <DropdownDivider />
              <DropdownItem className="text-danger" onClick={() => setShowDeleteModal(true)}>
                {labels.delete}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </div>
      )}

      {/* Main form */}
      <ToggleFieldForm
        initialData={formDataWithIcon}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onIconPickerOpen={handleIconPickerOpen}
        errors={errors}
        labels={labels}
        mode={mode}
        categories={categories}
        showCategorySelect={showCategorySelect}
        additionalActivityName={additionalActivityName}
        isSubmitting={isSubmitting}
      />

      {/* Goal card (edit mode only, not in field set) */}
      {mode === 'edit' && (
        <GoalCard
          goal={goal}
          labels={labels}
          goalNewUrl={goalNewUrl}
          goalEditUrl={goalEditUrl}
          goalDeleteUrl={goalDeleteUrl}
          fieldSetId={currentFieldSetId}
          editFieldSetUrl={editFieldSetUrl}
          csrfToken={csrfToken}
        />
      )}

      {/* Icon picker modal */}
      {showIconPicker && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Icon</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowIconPicker(false)}
                ></button>
              </div>
              <div className="modal-body">
                <IconPickerContainer
                  graphqlEndpoint={graphqlEndpoint}
                  csrfToken={csrfToken}
                  initialIconKey={formIcon}
                  initialScore={formScore}
                  onSelect={handleIconSelect}
                  onScoreChange={handleScoreChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      <ToggleFieldDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        labels={labels}
        isDeleting={isDeleting}
      />

      {/* Change category modal */}
      <ChangeCategoryModal
        isOpen={showChangeCategoryModal}
        onClose={() => setShowChangeCategoryModal(false)}
        onConfirm={handleChangeCategory}
        categories={categories}
        currentCategoryId={initialData.categoryId}
        labels={labels}
        isChanging={isChangingCategory}
      />

      {/* Move to field set modal */}
      <MoveToFieldSetModal
        isOpen={showMoveToFieldSetModal}
        onClose={() => setShowMoveToFieldSetModal(false)}
        onConfirm={handleMoveToFieldSet}
        fieldSets={fieldSets}
        labels={labels}
        isMoving={isMovingToFieldSet}
      />
    </>
  );
}

function ToggleFieldFormContainer(props: ToggleFieldFormContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <ToggleFieldFormContainerInner {...props} />
    </QueryClientProvider>
  );
}

// Store root reference for cleanup
let formRoot: Root | null = null;

/**
 * Mount function for rendering from Rails
 */
export function mountToggleFieldForm(
  container: HTMLElement,
  props: ToggleFieldFormContainerProps
): Root {
  if (formRoot) {
    formRoot.unmount();
  }

  const root = createRoot(container);
  formRoot = root;
  root.render(<ToggleFieldFormContainer {...props} />);
  return root;
}

/**
 * Unmount function for cleanup
 */
export function unmountToggleFieldForm(): void {
  if (formRoot) {
    formRoot.unmount();
    formRoot = null;
  }
}

export default ToggleFieldFormContainer;
